import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Headline5 from '../Typography/Heading/Headline5';
import Subtitle2, { Subtitle2Properties } from '../Typography/SubTitle2';
import Button from '../Button';
import { CardContainer } from '../Card';
import Anchor from '../Anchor';
import breakpoints from '../Theme/breakpoints';

const ProductCardContainer = styled(CardContainer)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  width: 144px;
  height: 232px;
`;

const ProductCardHeadline = styled(Headline5)`
 && {
  margin: 4px 0 0;
  padding: 0 2px;
  height: 2em;
  line-height: 1;
  overflow: hidden;
  font-size: 16px;
  font-family: "Futura PT Web Condensed Bold","Open Sans Condensed",sans-serif;
  flex: auto;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
 }
`;

const ProductButton = styled(Button)<{ rel?: string; target?: string }>`
&& {
  margin: 0;
  padding: 0;
  min-height: 40px;
  text-align: center;
  overflow: visible;
  color: #ea0051;
  line-height: 1;
  flex: initial;
  font-size: 14px;
  font-family: "Futura PT Web Condensed Bold","Open Sans Condensed",sans-serif;
}
&&:hover {
  background-color: transparent;
}
&&:before {
  background-color: #ffffff;
}
`;

const ProductLinkWrapper = styled.a`
  text-decoration: none;
`;

const relatedProductCardJoomlaPropertyTypes = {
  analyticsClassNames: PropTypes.arrayOf(
    PropTypes.string,
  ).isRequired,
  retailer: PropTypes.string,
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  graphic: PropTypes.node.isRequired,
  callToActionMessage: PropTypes.string,
};

type RelatedProductCardJoomlaProperties = InferProps<
  typeof relatedProductCardJoomlaPropertyTypes
>;

const RelatedProductCardJoomla = ({
  name,
  graphic,
  retailer,
  url,
  analyticsClassNames,
  callToActionMessage,
  ...rest
}: RelatedProductCardJoomlaProperties) => (
  <ProductCardContainer
    {...{ ...rest, className: 'related-product-card' }}
  >
    <ProductLinkWrapper
      className={
        analyticsClassNames.join(' ')
      }
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      { graphic }
      <ProductCardHeadline className="related-product-title">
        { name }
      </ProductCardHeadline>
    </ProductLinkWrapper>
    <ProductButton
      variant="text"
      size="small"
      className={
        [...analyticsClassNames, 'related-product-button'].join(' ')
      }
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      { callToActionMessage }
    </ProductButton>
  </ProductCardContainer>
);

RelatedProductCardJoomla.propTypes = relatedProductCardJoomlaPropertyTypes;

const StyledCardContainer = styled(CardContainer)`
  flex-basis: calc(48%);
  margin-bottom: 16px;
  padding: 0 4px;

  @media (min-width: ${breakpoints.sm}px) {
    flex-basis: calc(32%);
    padding: 0;
  }

  @media (min-width: ${breakpoints.md}px) {
    padding: 0 4px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    flex-basis: calc(48%);
    padding: 0;
  }
`;

const StyledSubtitle2 = styled(
  (properties: Subtitle2Properties) => <Subtitle2 {...properties} />,
)`
  margin: 8px 0 0;
  text-align: center;
  display: block;
`;

const StyledButton = styled(Button)`
  && {
    text-align: center;
    width: 100%;

    &:hover {
      background-color: transparent;
    }
  }
`;

const GraphicContainer = styled.div`
  transition: .25s ease-in-out;

  :hover {
    transform: scale(1.05)
  }
`;

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const StyledAnchor = styled(Anchor)`
  height: 100%;
`;

const relatedProductCardPropertyTypes = {
  graphic: PropTypes.node,
  retailer: PropTypes.string,
  callToActionMessage: PropTypes.string,
  productEventTracker: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  analyticsClassNames: PropTypes.arrayOf(
    PropTypes.string,
  ).isRequired,
  url: PropTypes.string.isRequired,
};

type RelatedProductCardProperties = InferProps<
  typeof relatedProductCardPropertyTypes
>;

const RelatedProductCard = ({
  name,
  graphic,
  retailer,
  url,
  analyticsClassNames,
  callToActionMessage,
  productEventTracker,
  ...rest
}: RelatedProductCardProperties) => (
  <StyledCardContainer
    {...{ ...rest, className: 'related-product-card' }}
  >
    <StyledAnchor
      className={
        analyticsClassNames.join(' ')
      }
      href={url}
      onClick={productEventTracker}
      target="_blank"
      styled
    >
      <Container>
        <GraphicContainer>
          { graphic }
        </GraphicContainer>
        <div>
          <StyledSubtitle2 className="related-product-title">
            { name }
          </StyledSubtitle2>
          {callToActionMessage
            && (
            <StyledButton
              variant="text"
              size="small"
              className={
                [...analyticsClassNames, 'related-product-button'].join(' ')
              }
            >
              { callToActionMessage }
            </StyledButton>
            )}
        </div>
      </Container>
    </StyledAnchor>
  </StyledCardContainer>
);

RelatedProductCard.propTypes = relatedProductCardPropertyTypes;

export { RelatedProductCardJoomla, RelatedProductCard };
